/**
 * n8n Module UI
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import i18n from '../../lib/i18n';
import { api } from '../../lib/api';
import {
  AsyncStatusToggle,
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  PageHeader,
  StatCard,
  StatsGrid,
  StatusDot,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '../../components/ui';
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
  Globe,
  History,
  Loader2,
  Play,
  RefreshCw,
  Settings,
  Trash2,
  Workflow,
  XCircle,
} from 'lucide-react';
import { themedConfirm } from '../../lib/themed-dialogs';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface N8nWorkflow {
  id: string;
  name: string;
  active: boolean;
  updatedAt: string;
  tags?: string[];
  webhooks?: { path: string; method: string; url: string }[];
}

interface N8nWorkflowDetail extends N8nWorkflow {
  webhooks?: { path: string; method: string; url: string }[];
}

interface N8nExecution {
  id: string;
  workflowId: string;
  workflowName?: string;
  status: 'new' | 'running' | 'success' | 'error' | 'waiting' | 'canceled';
  startedAt: string;
  stoppedAt: string | null;
  mode: string;
}

interface N8nConfig {
  url: string;
  editor_url: string;
  email: string;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function execBadgeVariant(status: string) {
  const map: Record<string, 'green' | 'red' | 'blue' | 'yellow' | 'gray' | 'purple'> = {
    success: 'green',
    error: 'red',
    running: 'blue',
    waiting: 'yellow',
    canceled: 'gray',
    new: 'purple',
  };
  return map[status] ?? 'gray';
}

function copyText(text: string) {
  navigator.clipboard.writeText(text).then(() => toast.success(i18n.t('n8n.copied')));
}

function formatDuration(startedAt: string, stoppedAt: string | null): string {
  if (!stoppedAt) return '';
  const ms = new Date(stoppedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 1000) return `${ms}ms`;
  return `${Math.round(ms / 1000)}s`;
}

// ─── Workflow Card ──────────────────────────────────────────────────────────────

function WorkflowCard({ wf }: { wf: N8nWorkflow }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);

  const { data: detail } = useQuery({
    queryKey: ['n8n-workflow', wf.id],
    queryFn: () =>
      api.mcp<{
        ok: boolean;
        workflow: N8nWorkflowDetail;
        webhooks: { path: string; method: string; url: string }[];
      }>('n8n.get_workflow', { id: wf.id }),
    enabled: expanded,
    staleTime: 60_000,
  });

  const toggleMut = useMutation({
    mutationFn: (active: boolean) => api.mcp('n8n.activate_workflow', { id: wf.id, active }),
    onSuccess: (_data, active) => {
      qc.invalidateQueries({ queryKey: ['n8n-workflows'] });
      toast.success(active ? t('n8n.workflowActivated') : t('n8n.workflowDeactivated'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.mcp('n8n.delete_workflow', { id: wf.id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['n8n-workflows'] });
      toast.success(t('n8n.workflowDeleted'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const webhooks = detail?.webhooks ?? [];

  return (
    <Card>
      <CardContent className='p-0'>
        {/* Header row */}
        <div
          className='flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-800/30 transition-colors rounded-t-xl'
          onClick={() => setExpanded((e) => !e)}
        >
          <StatusDot status={wf.active ? 'active' : 'stopped'} />
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-medium text-white truncate'>{wf.name}</p>
            <p className='text-[10px] text-gray-600 mt-0.5 font-mono'>
              {wf.id}
              {wf.tags && wf.tags.length > 0 && (
                <span className='ml-2 not-mono text-gray-500'>
                  {wf.tags.map((tag) => `#${tag}`).join(' ')}
                </span>
              )}
            </p>
          </div>
          <div
            className='flex items-center gap-2 flex-shrink-0'
            onClick={(e) => e.stopPropagation()}
          >
            <span className='text-[10px] text-gray-600'>
              {new Date(wf.updatedAt).toLocaleDateString('cs-CZ')}
            </span>
            <Button
              variant='ghost'
              size='icon'
              loading={deleteMut.isPending}
              onClick={async () => {
                if (await themedConfirm(t('n8n.confirmDeleteWorkflow', { name: wf.name }))) deleteMut.mutate();
              }}
              title={t('n8n.delete')}
              className='text-gray-600 hover:text-red-400'
            >
              <Trash2 size={13} />
            </Button>
            <AsyncStatusToggle
              value={wf.active}
              pending={toggleMut.isPending}
              onToggle={(v) => toggleMut.mutateAsync(v)}
              onError={(e) => toast.error(e instanceof Error ? e.message : String(e))}
            />
          </div>
          <div className='text-gray-600 flex-shrink-0'>
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </div>
        </div>

        {/* Expanded: webhooks */}
        {expanded && (
          <div className='border-t border-gray-800 px-4 pb-4 pt-3 space-y-3'>
            {!detail && (
              <div className='flex justify-center py-3'>
                <Loader2 size={16} className='animate-spin text-gray-600' />
              </div>
            )}
            {detail && webhooks.length > 0 && (
              <div>
                <p className='text-[10px] text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1'>
                  <Globe size={10} /> {t('n8n.webhookTriggers')}
                </p>
                <div className='space-y-1.5'>
                  {webhooks.map((wh, i) => (
                    <div
                      key={i}
                      className='flex items-center gap-2 bg-gray-900 rounded-lg px-3 py-2'
                    >
                      <Badge variant='yellow'>{wh.method}</Badge>
                      <span className='text-xs text-gray-300 font-mono flex-1 truncate'>
                        {wh.url}
                      </span>
                      <Button
                        variant='ghost'
                        size='icon'
                        onClick={() => copyText(wh.url)}
                        title={t('n8n.copyUrl')}
                      >
                        <Copy size={11} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {detail && webhooks.length === 0 && (
              <p className='text-xs text-gray-600'>{t('n8n.noWebhookNodes')}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Executions Tab ─────────────────────────────────────────────────────────────

function ExecutionsTab() {
  const { t } = useTranslation();
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['n8n-executions'],
    queryFn: () =>
      api.mcp<{ ok: boolean; executions: N8nExecution[] }>('n8n.list_executions', { limit: 50 }),
    refetchInterval: 15_000,
  });

  const executions = data?.executions ?? [];

  return (
    <div className='space-y-3'>
      <div className='flex justify-end'>
        <Button
          variant='ghost'
          size='icon'
          onClick={() => refetch()}
          loading={isFetching}
          title={t('common.refresh')}
        >
          <RefreshCw size={14} />
        </Button>
      </div>

      {isLoading && (
        <div className='flex justify-center py-10'>
          <Loader2 size={20} className='animate-spin text-gray-600' />
        </div>
      )}

      {!isLoading && executions.length === 0 && (
        <EmptyState
          icon={<History size={24} />}
          title={t('n8n.noExecutions')}
          description={t('n8n.executionsHint')}
        />
      )}

      <div className='space-y-2'>
        {executions.map((e) => (
          <Card key={e.id}>
            <CardContent className='px-4 py-3'>
              <div className='flex items-center justify-between gap-3'>
                <div className='min-w-0 flex-1'>
                  <p className='text-sm text-white truncate'>{e.workflowName ?? e.workflowId}</p>
                  <p className='text-[10px] text-gray-500 mt-0.5 flex items-center gap-2'>
                    <span>{new Date(e.startedAt).toLocaleString('cs-CZ')}</span>
                    {e.stoppedAt && (
                      <span className='text-gray-600'>
                        {formatDuration(e.startedAt, e.stoppedAt)}
                      </span>
                    )}
                    <span className='text-gray-700'>{e.mode}</span>
                  </p>
                </div>
                <Badge variant={execBadgeVariant(e.status)} dot>{e.status}</Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── Settings Tab ───────────────────────────────────────────────────────────────

function SettingsTab({ status }: {
  status: { ok: boolean; version?: string; error?: string; config?: N8nConfig } | undefined;
}) {
  const { t } = useTranslation();
  const editorUrl = status?.config?.editor_url ?? `https://${window.location.hostname}:5678`;

  return (
    <div className='space-y-4'>
      {/* Connection status */}
      <Card>
        <CardContent className='p-5 space-y-4'>
          <div className='flex items-center gap-3'>
            {status?.ok
              ? <CheckCircle2 size={18} className='text-emerald-400 flex-shrink-0' />
              : <XCircle size={18} className='text-red-400 flex-shrink-0' />}
            <div className='min-w-0'>
              <p className='text-sm font-medium text-white'>
                {status?.ok
                  ? t('n8n.connectedVersion', { version: status.version ?? '?' })
                  : t('n8n.unavailable')}
              </p>
              {status?.error && <p className='text-xs text-red-400 mt-0.5'>{status.error}</p>}
            </div>
            <div className='ml-auto flex-shrink-0'>
              <StatusDot
                status={status?.ok ? 'running' : 'error'}
                withLabel
                label={status?.ok ? t('n8n.statusOnline') : t('n8n.statusOffline')}
              />
            </div>
          </div>

          <div className='border-t border-gray-800 pt-4 space-y-2'>
            <p className='text-xs text-gray-500 font-medium uppercase tracking-wider'>
              {t('n8n.connection')}
            </p>
            <div className='bg-gray-900 rounded-xl p-3 font-mono text-xs space-y-1.5'>
              <div className='flex gap-2'>
                <span className='text-gray-500 w-16 flex-shrink-0'>url</span>
                <span className='text-gray-300'>
                  {status?.config?.url ?? 'http://localhost:5678'}
                </span>
              </div>
              <div className='flex gap-2'>
                <span className='text-gray-500 w-16 flex-shrink-0'>email</span>
                <span className='text-gray-300'>{status?.config?.email || '—'}</span>
              </div>
              <div className='flex gap-2'>
                <span className='text-gray-500 w-16 flex-shrink-0'>editor</span>
                <span className='text-gray-300'>{editorUrl}</span>
              </div>
            </div>
            <p className='text-[10px] text-gray-600'>
              {t('n8n.configHint')} <code className='text-gray-500'>n8n_url</code>,
              {' '}
              <code className='text-gray-500'>n8n_email</code>,{' '}
              <code className='text-gray-500'>n8n_password</code>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* n8n Editor link */}
      <Card>
        <CardContent className='p-5'>
          <p className='text-xs text-gray-500 font-medium uppercase tracking-wider mb-3'>
            n8n Editor
          </p>
          <p className='text-xs text-gray-500 mb-4'>
            {t('n8n.editorHint')}
          </p>
          <Button
            variant='primary'
            size='md'
            icon={<ExternalLink size={14} />}
            onClick={() => window.open(editorUrl, '_blank')}
          >
            {t('n8n.openEditor')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function N8nPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data: wfData, isLoading: wfLoading } = useQuery({
    queryKey: ['n8n-workflows'],
    queryFn: () =>
      api.mcp<{ ok: boolean; workflows: N8nWorkflow[] }>('n8n.list_workflows', {}),
    refetchInterval: 30_000,
  });

  const { data: statusData } = useQuery({
    queryKey: ['n8n-status'],
    queryFn: async () => {
      // status: no MCP tool (testConnection only on REST) — keep REST for version probe
      const [status, cfg] = await Promise.all([
        api.mcp<{ ok: boolean; version?: string; error?: string }>('n8n.status', {}),
        api.mcp<{ ok: boolean; config: { url: string; email: string } }>('n8n.get_config', {}),
      ]);
      const editor_url = `https://${window.location.hostname}:5678`;
      return {
        ...status,
        config: {
          url: cfg.config.url,
          email: cfg.config.email,
          editor_url,
        } satisfies N8nConfig,
      };
    },
  });

  const { data: execData } = useQuery({
    queryKey: ['n8n-executions-stats'],
    queryFn: () =>
      api.mcp<{ ok: boolean; executions: N8nExecution[] }>('n8n.list_executions', { limit: 100 }),
    refetchInterval: 15_000,
  });

  const workflows = wfData?.workflows ?? [];
  const executions = execData?.executions ?? [];
  const activeCount = workflows.filter((w) => w.active).length;
  const runningCount = executions.filter((e) => e.status === 'running').length;
  const errorCount = executions.filter((e) => e.status === 'error').length;
  const editorUrl = statusData?.config?.editor_url ?? `https://${window.location.hostname}:5678`;

  return (
    <div className='space-y-6'>
      <PageHeader
        title={t('n8n.pageTitle')}
        description={t('n8n.pageDescription')}
        icon={<Workflow size={20} className='text-orange-400' />}
        actions={
          <Button
            variant='ghost'
            size='icon'
            onClick={() => qc.invalidateQueries()}
            title={t('common.refresh')}
          >
            <RefreshCw size={15} />
          </Button>
        }
      />

      <StatsGrid columns={4}>
        <StatCard label={t('n8n.workflows')} value={workflows.length} icon={Workflow} />
        <StatCard label={t('n8n.statActive')} value={activeCount} icon={Activity} variant='success' />
        <StatCard label={t('n8n.statRunning')} value={runningCount} icon={Play} variant='info' />
        <StatCard label={t('n8n.statErrors')} value={errorCount} icon={AlertCircle} variant='error' />
      </StatsGrid>

      <Tabs defaultTab='workflows'>
        <TabList>
          <Tab id='workflows' icon={<Workflow size={14} />}>{t('n8n.workflows')}</Tab>
          <Tab id='executions' icon={<History size={14} />}>{t('n8n.executions')}</Tab>
          <Tab id='settings' icon={<Settings size={14} />}>{t('n8n.settings')}</Tab>
        </TabList>

        <TabPanel id='workflows'>
          {wfLoading && (
            <div className='flex justify-center py-10'>
              <Loader2 size={20} className='animate-spin text-gray-600' />
            </div>
          )}
          {!wfLoading && workflows.length === 0 && (
            <EmptyState
              icon={<Workflow size={24} />}
              title={t('n8n.noWorkflows')}
              description={t('n8n.noWorkflowsHint')}
              action={
                <Button
                  variant='secondary'
                  size='sm'
                  icon={<ExternalLink size={13} />}
                  onClick={() => window.open(editorUrl, '_blank')}
                >
                  {t('n8n.openEditor')}
                </Button>
              }
            />
          )}
          {!wfLoading && workflows.length > 0 && (
            <div className='space-y-2'>
              {workflows.map((wf) => <WorkflowCard key={wf.id} wf={wf} />)}
            </div>
          )}
        </TabPanel>

        <TabPanel id='executions' keepMounted>
          <ExecutionsTab />
        </TabPanel>

        <TabPanel id='settings'>
          <SettingsTab status={statusData} />
        </TabPanel>
      </Tabs>
    </div>
  );
}
